import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HttpStatusCode } from "axios";

import {
  RentalStockItem,
  RentalStockListSortOrder,
  deleteRentalStock,
  getRentalStockList,
  getStringFromRentalStockGoodsTypeId,
} from "../../../../../services/rentalStockServices";

import useFullScreenLoading from "../../../../../hooks/useFullScreenLoading";
import useStoreHeader from "../../../../../hooks/useStoreHeader";
import useSimplePopup from "../../../../../hooks/useSimplePopup";
import useInfiniteScroll, {
  INFINITE_CODE,
  InfiniteScrollCallbackArgs,
} from "../../../../../hooks/useInfiniteScroll";

import { GoToTop, NewPostIcon } from "../../../../Talk";
import CommonButton from "../../../../../components/Button";
import VerticalRule from "../../../../../components/Rule/Vertical";
import CommonPopup from "../../../../../components/Popup";
import BottomGroup from "../../../../../components/Button/Group";
import ServiceRentItemImage from "../../../../../components/Service/Rent/ItemImage";

import { PostListNoPostIcon } from "../../../../../components/Talk/PostList";

type Props = {};

export default function RentalStockConnect(props: Props) {
  const scrollDivRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const navigate = useNavigate();
  const [popup, setPopup] = useState<string>("");
  const [simplePopupComponent, { open }] = useSimplePopup({});
  const PAGE_SIZE = 10;
  const SCROLL_LOAD_START_PAGE = 2;

  const [rentalStockList, setRentalStockList] = useState<RentalStockItem[]>([]);
  const [rentalStock, setRentalStock] = useState<RentalStockItem | undefined>(
    undefined
  );

  const loadRentalStockList = async (args?: InfiniteScrollCallbackArgs) => {
    let itemLength = INFINITE_CODE.ERROR;

    setIsLoading(true);
    try {
      const result = await getRentalStockList({
        sort: "createdAt.desc" as RentalStockListSortOrder,
        storeId: storeId,
        ...args,
      });

      if (result.status === HttpStatusCode.Ok) {
        itemLength = result.data.items.length;
        if (args?.page === 1) {
          setRentalStockList(result.data.items);
        } else {
          setRentalStockList((prev) => [...(prev ?? []), ...result.data.items]);
        }
      }
    } catch (e) {
      setRentalStockList([]);
      console.log(e);
    } finally {
      setIsLoading(false);
    }

    return itemLength;
  };

  const [, { resetScroll }] = useInfiniteScroll({
    isLoading,
    callback: loadRentalStockList,
    startPage: SCROLL_LOAD_START_PAGE,
    pageSize: PAGE_SIZE,
  });

  const onClickRefresh = async () => {
    resetScroll();
    await loadRentalStockList({
      page: SCROLL_LOAD_START_PAGE - 1,
      pageSize: PAGE_SIZE,
    });
  };

  const [{ storeId, isEmpty }, storeHeader] = useStoreHeader({
    title: "대여 품목 연결 관리",
    onClickRefresh,
  });

  const handleRentalStockChange = (
    rentalStock: RentalStockItem,
    isActive = true
  ) => {
    if (isActive) {
      setRentalStock(rentalStock);
      setPopup("delete");
    } else {
      open("연결 해제 할 수 없는 물품입니다.");
    }
  };

  const handleConfirmDelete = async () => {
    if (!rentalStock) return;
    try {
      const result = await deleteRentalStock(rentalStock.stockId);

      if (result.status === HttpStatusCode.Ok) {
        setPopup("");
        open("연결 해제 되었습니다.");
      }
    } catch (e) {
      open("연결 해제에 실패했습니다.");
      console.log(e);
    } finally {
      await onClickRefresh();
    }
  };

  useEffect(() => {
    scrollDivRef.current?.scrollIntoView();
    onClickRefresh();
  }, [storeId]);

  return (
    <div ref={scrollDivRef}>
      {loadingComponent}
      {storeHeader}
      <div className="px-5">
        <div className="py-5">
          <div className="flex flex-col text-lg font-medium">물품 목록</div>
        </div>
        {rentalStockList.length > 0 ? (
          <div className="flex flex-col gap-3 w-max-screen">
            {rentalStockList.map((item, index) => (
              <RentalStockListItemImage
                key={index}
                item={item}
                onClick={handleRentalStockChange}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center w-full h-40 gap-3 text-brand-neutral-60">
            <PostListNoPostIcon />
            등록된 물품이 없어요
          </div>
        )}
      </div>

      <CommonPopup isOpen={popup === "delete"} unUseHorizonRule padding={0}>
        <div className="px-5 py-5">
          <div className="flex flex-col text-center w-full font-medium leading-6 text-md h-max-[80px] py-[30px]">
            <div
              className="overflow-hidden"
              style={{
                WebkitLineClamp: 2,
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
              }}
            >
              {rentalStock?.goodsName}
            </div>
            <div className="">물품을 연결 해제 하시겠어요?</div>
          </div>
          <div className="pb-[50px]"></div>
          <BottomGroup zIndex="high">
            <div className="flex flex-row gap-[6px] h-bottom-button">
              <CommonButton
                variant="grey"
                className="w-[100px] rounded-none"
                onClick={() => setPopup("")}
              >
                취소
              </CommonButton>
              <CommonButton
                disabled={isLoading}
                className="w-full rounded-none"
                variant="primary"
                onClick={handleConfirmDelete}
              >
                확인
              </CommonButton>
            </div>
          </BottomGroup>
        </div>
      </CommonPopup>
      {simplePopupComponent}
      <div className="fixed bottom-5 right-5 flex flex-col gap-2.5 z-[19]">
        <div
          className="border border-brand-floating-secondary-button rounded-[10px] p-2 bg-white"
          onClick={() => {
            scrollDivRef.current?.scrollIntoView({ behavior: "smooth" });
          }}
          style={{ boxShadow: "2px 5px 4px 0px rgba(0, 0, 0, 0.10)" }}
        >
          <GoToTop />
        </div>

        <div
          className=" bg-brand-floating-primary-button p-2 rounded-[10px] h-[40px] w-[40px]"
          onClick={() => {
            if (isEmpty) {
              setPopup("no-store");
              open("연결할 상점이 없습니다.");
            } else {
              navigate("/manager/rental-manager/rental-stock/create", {
                state: { storeId: storeId },
              });
            }
          }}
          style={{ boxShadow: "2px 5px 4px 0px rgba(236, 30, 159, 0.10)" }}
        >
          <NewPostIcon />
        </div>
      </div>
    </div>
  );
}

export type RentalStockListItemImageProps = {
  item: RentalStockItem;
  onClick?: (item: RentalStockItem, isActive: boolean) => void;
};

export const RentalStockListItemImage = ({
  item,
  onClick,
}: RentalStockListItemImageProps) => {
  const isNotRemovable = (item: RentalStockItem) => {
    return (
      //재고 관리 필요 타입 추가 필요
      item.stockSummary.inUseCount > 0 || item.stockSummary.lostDamagedCount > 0
    );
  };

  return (
    <div
      className="flex flex-col p-5 border border-brand-neutral-10 rounded-[10px] w-full h-full"
      style={{ boxShadow: "0px 1px 5px 0px rgba(0, 0, 0, 0.08)" }}
    >
      <div className="flex flex-row gap-2.5 justify-between w-fit">
        <ServiceRentItemImage
          src={item?.image?.filePath}
          name={item.goodsName}
        />
        <div className="flex flex-col gap-1 min-w-[calc(100vw-205px)] w-[30vw]">
          <div className="flex flex-col gap-2.5">
            <div className="flex flex-row h-[22px] text-[15px] font-medium leading-[22px]">
              <div className="flex flex-col whitespace-nowrap">
                [{getStringFromRentalStockGoodsTypeId(item.goodsTypeId)}]
              </div>
              &nbsp;
              <div className="flex flex-col max-w-[calc(100%-42px)]">
                <div className="truncate">{item.goodsName}</div>
              </div>
            </div>
            <div className="flex flex-col text-xs text-brand-neutral-40 font-normal tracking-[-0.12px] leading-4 max-h-[31px] break-all">
              <div
                className="overflow-hidden"
                style={{
                  WebkitLineClamp: 2,
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                }}
              >
                {item.goodsDescription}
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-row gap-1.5 justify-between items-center">
          <div className="flex flex-col h-full">
            <VerticalRule className="!h-full bg-[#9F9F9F] opacity-20" />
          </div>
          <div
            className={clsx([
              "flex flex-col cursor-pointer",
              {
                "cursor-not-allowed opacity-60": isNotRemovable(item),
              },
            ])}
            onClick={() => {
              onClick?.(item, !isNotRemovable(item));
            }}
          >
            <TrashIcon />
          </div>
        </div>
      </div>
    </div>
  );
};

export const TrashIcon = () => (
  <svg
    width="24"
    height="25"
    viewBox="0 0 24 25"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M21 5.5H3"
      stroke="#9F9F9F"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M14 4.5H10"
      stroke="#9F9F9F"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M5 8.5V21.5H19C19 20.3182 19 8.5 19 8.5"
      stroke="#9F9F9F"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M14 8.5V18"
      stroke="#9F9F9F"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M10 8.5V18"
      stroke="#9F9F9F"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);
